<template>
  <v-card :outlined="!embedded" :flat="embedded" :class="{ 'elevation-0': embedded }">
    <v-card-title v-if="!embedded" class="text-subtitle-1">Nuovo Prodotto Stagione</v-card-title>

    <v-card-text>
      <v-form ref="form_prodotto" v-model="form_prodotto_valid">
        <div class="text-subtitle-2 mb-2">Componenti Prodotto</div>

        <v-alert
          v-if="!prodotto_form.componenti.length"
          type="warning"
          outlined
          dense
          class="mb-2"
        >
          Aggiungi almeno una specie.
        </v-alert>

        <v-card
          v-for="(componente, index) in prodotto_form.componenti"
          :key="componente.rowId"
          outlined
          class="pa-3 mb-2"
        >
          <v-row dense>
            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="componente.id_specie"
                :items="specie_options"
                item-text="text"
                item-value="value"
                label="Specie"
                :rules="[(v) => !!v || 'Campo obbligatorio']"
                outlined
                clearable
                hide-details
                @change="onSpecieChange(componente)"
              >
                <template #item="{ item, on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-avatar class="mr-2" size="32">
                      <v-img :src="item.img_url"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="24" class="mr-2">
                      <v-img :src="item.img_url"></v-img>
                    </v-avatar>
                    <span>{{ item.text }}</span>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="4">
              <v-select
                v-model="componente.id_serie"
                :items="serieOptions(componente.id_specie)"
                item-text="text"
                item-value="value"
                label="Serie (opzionale)"
                :disabled="!componente.id_specie"
                clearable
                outlined
                hide-details
                @change="onSerieChange(componente)"
              />
            </v-col>

            <v-col cols="12" md="8">
              <v-autocomplete
                v-model="componente.id_varieta"
                :items="varietaOptions(componente.id_specie, componente.id_serie)"
                item-text="text"
                item-value="value"
                label="Varietà (opzionale)"
                :disabled="!componente.id_specie"
                outlined
                clearable
                hide-details
                @change="onVarietaChange(componente)"
              >
                <template #item="{ item, on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-avatar class="mr-2" size="28">
                      <v-img :src="item.img_url"></v-img>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="20" class="mr-2">
                      <v-img :src="item.img_url"></v-img>
                    </v-avatar>
                    <span>{{ item.text }}</span>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" md="2">
              <v-text-field
                v-model.number="componente.ripeti"
                label="Ripeti"
                type="number"
                min="1"
                outlined
                hide-details
              />
            </v-col>

            <v-col cols="12" md="2" class="d-flex align-center justify-end">
              <v-btn
                icon
                color="red"
                :disabled="prodotto_form.componenti.length === 1"
                @click="removeComponente(index)"
              >
                <v-icon >mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>

        <v-btn text small color="primary" @click="addComponente">
          <v-icon small class="mr-1">mdi-plus</v-icon>
          Aggiungi specie
        </v-btn>

        <v-divider class="my-3" />
        <v-row no-gutters>
          <v-col cols="12" md="5">
            <v-select
              v-model="prodotto_form.id_contenitore"
              :items="contenitori_options"
              item-text="text"
              item-value="value"
              label="Contenitore"
              :rules="[(v) => !!v || 'Campo obbligatorio']"
              outlined
              @change="onContenitoreChange"
            />
          </v-col>
          <v-col cols="12" md="4" class="pl-2">
            <v-select
              v-model="prodotto_form.colore_contenitore"
              :items="colori_contenitore_options"
              item-text="label"
              item-value="value"
              label="Colore vaso"
              :disabled="!prodotto_form.id_contenitore || !colori_contenitore_options.length"
              :rules="[
                (v) => !colori_contenitore_options.length || !!v || 'Seleziona un colore vaso',
              ]"
              outlined
            >
              <template #item="{ item, on, attrs }">
                <v-list-item v-bind="attrs" v-on="on">
                  <span class="mr-2" :style="swatchStyle(item.hex)"></span>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template #selection="{ item }">
                <div class="d-flex align-center">
                  <span class="mr-2" :style="swatchStyle(item.hex)"></span>
                  <span>{{ item.label }}</span>
                </div>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="3" class="pl-2">
            <v-autocomplete
              v-model.number="prodotto_form.settimana_trapianto"
              :items="settimane_trapianto_options"
              label="Sett. trapianto"
              clearable
              :rules="[
                (v) => !!v || 'Campo obbligatorio',
                (v) => (Number(v) >= 1 && Number(v) <= 52) || 'Valore da 1 a 52',
              ]"
              outlined
            />
          </v-col>
          <v-col cols="12" md="5" class="d-flex align-center">
            <v-text-field
              v-model="prodotto_form.quantita_produzione"
              label="Qta prod."
              type="number"
              min="0"
              :rules="[
                (v) => v !== null && v !== undefined && v !== '' || 'Campo obbligatorio',
              ]"
              outlined
            />
          </v-col>
        </v-row>
      </v-form>

      <v-alert v-if="!contenitori_options.length" type="warning" outlined dense class="mt-3 mb-2">
        Nessun contenitore disponibile. Crea prima un contenitore.
      </v-alert>

      <v-alert v-if="!specie_options.length" type="warning" outlined dense>
        Nessuna specie disponibile. Verifica il caricamento delle specie.
      </v-alert>

      <div style="min-height: 50px;" class="mt-3 d-flex align-end">
        <v-alert
          v-if="feedback_visible && feedback_message"
          :type="feedback_type"
          dense
          outlined
          class="mb-0 w-100"
          style="width: 100%;"
        >
          {{ feedback_message }}
        </v-alert>
      </div>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        :loading="loading_esterno"
        :disabled="!can_add_prodotto || loading_esterno"
        @click="salva"
      >
        {{ isModifica ? "Aggiorna prodotto" : "Salva prodotto" }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import colorUtility from "../Mixin/colorUtility";
import specieLookup from "../Mixin/specieLookup";

export default {
  name: "NuovoProdottoForm",
  mixins: [colorUtility, specieLookup],
  props: {
    stagione_attiva: { type: Object, default: null },
    specie_disponibili: { type: Array, default: () => [] },
    contenitori_coltivazione: { type: Array, default: () => [] },
    prodotti_stagione_attiva: { type: Array, default: () => [] },
    prodotto_in_modifica: { type: Object, default: null },
    presets: { type: Object, default: () => ({}) },
    loading_esterno: { type: Boolean, default: false },
    error_esterno: { type: String, default: "" },
    success_esterno: { type: String, default: "" },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      form_prodotto_valid: false,
      save_error_message: "",
      feedback_visible: false,
      prodotto_form: {
        id_contenitore: null,
        colore_contenitore: null,
        quantita_produzione: null,
        settimana_trapianto: null,
        componenti: [],
      },
    };
  },
  computed: {
    isModifica() {
      return !!this.prodotto_in_modifica;
    },
    contenitori_options() {
      return this.contenitori_coltivazione.map((contenitore) => ({
        value: contenitore.id,
        text: `${contenitore.tipo} (Ø ${this.formatNumber(contenitore.diametro)})`,
      }));
    },
    colori_contenitore_options() {
      const contenitore = this.contenitori_coltivazione.find(
        (item) => item.id == this.prodotto_form.id_contenitore
      );
      if (!contenitore || !Array.isArray(contenitore.colori)) return [];
      return contenitore.colori
        .map((entry) => this.parseColorEntry(entry))
        .filter((item) => !!item && !!item.value);
    },
    specie_options() {
      return this.specie_disponibili.map((specie) => ({
        value: specie.id,
        text: specie.nome,
        img_url: `${process.env.VUE_APP_API_ROOT}/specie/${specie.id}/img?md5=${specie.img_md5 || ""}`,
      }));
    },
    settimane_trapianto_options() {
      return Array.from({ length: 52 }, (_, index) => index + 1);
    },
    can_add_prodotto() {
      return (
        !!this.stagione_attiva &&
        this.contenitori_options.length > 0 &&
        this.specie_options.length > 0 &&
        this.prodotto_form.componenti.length > 0
      );
    },
    feedback_message() {
      return this.save_error_message || this.error_esterno || this.success_esterno || "";
    },
    feedback_type() {
      if (this.save_error_message || this.error_esterno) return "error";
      if (this.success_esterno) return "success";
      return "info";
    },
  },
  watch: {
    feedback_message(val) {
      clearTimeout(this.$_feedbackTimer);
      if (val) {
        this.feedback_visible = true;
        this.$_feedbackTimer = setTimeout(() => {
          this.feedback_visible = false;
          this.save_error_message = "";
          this.$emit("clear-feedback");
        }, 2500);
      } else {
        this.feedback_visible = false;
      }
    },
  },
  created() {
    this.$_feedbackTimer = null;
  },
  mounted() {
    if (this.prodotto_in_modifica) {
      this.popolaDaModifica(this.prodotto_in_modifica);
    } else {
      this.resetForm();
    }
  },
  beforeDestroy() {
    clearTimeout(this.$_feedbackTimer);
  },
  methods: {
    resetForm() {
      const primoComponente = this.createEmptyComponente();
      if (this.presets && this.presets.id_specie) {
        const specieOpt = this.specie_options.find(
          (opt) => String(opt.value) === String(this.presets.id_specie)
        );
        if (specieOpt) primoComponente.id_specie = specieOpt.value;
      }
      this.prodotto_form = {
        id_contenitore: (() => {
          if (!this.presets || !this.presets.id_contenitore) return null;
          const opt = this.contenitori_options.find(
            (o) => String(o.value) === String(this.presets.id_contenitore)
          );
          return opt ? opt.value : null;
        })(),
        colore_contenitore: null,
        quantita_produzione: null,
        settimana_trapianto: (this.presets && this.presets.settimana_trapianto) || null,
        componenti: [primoComponente],
      };
      if (this.prodotto_form.id_contenitore) {
        this.onContenitoreChange();
      }
      if (this.$refs.form_prodotto) {
        this.$refs.form_prodotto.resetValidation();
      }
    },
    createEmptyComponente() {
      return {
        rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id_specie: null,
        id_serie: null,
        id_varieta: null,
        id_colore: null,
        ripeti: 1,
      };
    },
    addComponente() {
      this.prodotto_form.componenti.push(this.createEmptyComponente());
    },
    removeComponente(index) {
      this.prodotto_form.componenti.splice(index, 1);
    },
    onSpecieChange(componente) {
      componente.id_serie = null;
      componente.id_varieta = null;
      componente.id_colore = null;
    },
    onSerieChange(componente) {
      componente.id_varieta = null;
      componente.id_colore = null;
    },
    onVarietaChange(componente) {
      const varieta = this.findVarietaById(componente.id_specie, componente.id_varieta);
      componente.id_colore = varieta ? varieta.id_colore : null;
    },
    onContenitoreChange() {
      const isCurrentColorValid = this.colori_contenitore_options.some(
        (item) => item.value === this.prodotto_form.colore_contenitore
      );
      if (!isCurrentColorValid) {
        this.prodotto_form.colore_contenitore =
          (this.colori_contenitore_options[0] || {}).value || null;
      }
    },
    validateComponenti() {
      return this.prodotto_form.componenti.every((c) => !!c.id_specie);
    },
    normalizeDupValue(value) {
      if (value === null || value === undefined || value === "") return "na";
      return String(value);
    },
    prodottoDupKey(prodotto) {
      const idContenitore = this.normalizeDupValue(prodotto.id_contenitore);
      const coloreContenitore = this.normalizeDupValue(prodotto.colore_contenitore);
      const settimanaTrapianto = this.normalizeDupValue(prodotto.settimana_trapianto);
      const componentiKey = (prodotto.componenti || [])
        .map((c) => {
          return `${this.normalizeDupValue(c.id_specie)}-${this.normalizeDupValue(c.id_serie)}-${this.normalizeDupValue(c.id_varieta)}`;
        })
        .sort()
        .join("|");
      return `${idContenitore}::${coloreContenitore}::${settimanaTrapianto}::${componentiKey}`;
    },
    isDuplicateProdotto(payload) {
      const currentKey = this.prodottoDupKey(payload);
      const idDaEscludere = this.isModifica && this.prodotto_in_modifica ? this.prodotto_in_modifica.id : null;
      return this.prodotti_stagione_attiva.some(
        (prodotto) =>
          prodotto.id !== idDaEscludere && this.prodottoDupKey(prodotto) === currentKey
      );
    },
    buildProdottoPayload() {
      const componentiEspansi = [];
      this.prodotto_form.componenti.forEach((c) => {
        const ripeti = Number(c.ripeti) || 1;
        const componente = {
          id_specie: c.id_specie,
          id_serie: c.id_serie || null,
          id_varieta: c.id_varieta,
          id_colore: c.id_colore,
        };
        for (let i = 0; i < ripeti; i++) {
          componentiEspansi.push({ ...componente });
        }
      });
      return {
        id_stagione: this.stagione_attiva.id,
        id_contenitore: this.prodotto_form.id_contenitore,
        colore_contenitore: this.prodotto_form.colore_contenitore,
        quantita_produzione: Number(this.prodotto_form.quantita_produzione),
        settimana_trapianto: this.prodotto_form.settimana_trapianto,
        componenti: componentiEspansi,
      };
    },
    popolaDaModifica(prodotto) {
      const componentiForm = (prodotto.componenti || []).map((c) => ({
        rowId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        id_specie: c.id_specie || null,
        id_serie: c.id_serie || null,
        id_varieta: c.id_varieta || null,
        id_colore: c.id_colore || null,
        ripeti: 1,
      }));
      this.prodotto_form = {
        id_contenitore: prodotto.id_contenitore || null,
        colore_contenitore: prodotto.colore_contenitore || null,
        quantita_produzione: prodotto.quantita_produzione != null ? prodotto.quantita_produzione : null,
        settimana_trapianto: prodotto.settimana_trapianto || null,
        componenti: componentiForm.length ? componentiForm : [this.createEmptyComponente()],
      };
      this.$nextTick(() => {
        if (this.$refs.form_prodotto) this.$refs.form_prodotto.resetValidation();
      });
    },
    salva() {
      if (!this.$refs.form_prodotto.validate() || !this.stagione_attiva) return;
      if (!this.validateComponenti()) {
        this.save_error_message = "Seleziona la specie per ogni componente.";
        return;
      }
      this.save_error_message = "";
      const payload = this.buildProdottoPayload();
      if (this.isDuplicateProdotto(payload)) {
        this.save_error_message =
          "Prodotto duplicato: stessa specie/serie/varietà, contenitore, colore vaso e settimana di trapianto già presenti.";
        return;
      }
      if (this.isModifica) {
        this.$emit("prodotto-modificato", payload);
      } else {
        this.$emit("prodotto-aggiunto", payload);
      }
    },
  },
};
</script>
